import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, FieldProps, Form, Formik } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";

import ArrowIcon from "@/assets/ArrowIcon";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import Loader from "@/components/Layouts/Loader";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import { getListOfTypeRefData, getRefDataById, postRefData, putRefData } from "@/services/referenceDataService";
import { addRefDataSchema } from "@/schemas/addRefDataSchema";
import SelectTypeRefData from "@/components/Selects/SelectTypeRefData";

const EditReferenceData = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();

    const { id, returnPath } = location.state || {};

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [cancelButtonText, setCancelButtonText] = useState<string>("Cancel");
    const [isLoading, setIsLoading] = useState(false);
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [listType, setListType] = useState<any[]>([]);

    const navigateBack = () => {
        navigate(returnPath || '/settings/reference-data');
    }

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Changes?");
        setConfirmationTest("Are you sure you want to discard all changes?");
        setConfirmationTitleButton("Yes");
        setCancelButtonText("No");
    }

    const handleSubmit = (data: any) => {
        setTitle("Save Reference Data?");
        setConfirmationTest("Are you sure you want to save?");
        setConfirmationTitleButton("Save");
        setCancelButtonText("Cancel");
        onOpen();
        setIsSubmitting(true);
        setDataEdit(data);
    }

    const generateDataAdd = () => {
        const data = {
            type: dataEdit?.type || '',
            order: dataEdit?.order || '',
            value: dataEdit?.value || '',
            name: dataEdit?.name || '',
        };

        return data;
    }

    const generateDataEdit = () => {
        const data = {
            id,
            type: dataEdit?.type || '',
            order: dataEdit?.order || '',
            value: dataEdit?.value || '',
            name: dataEdit?.name || '',
        };

        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel Changes?") {
            navigateBack();
            return;
        }

        setIsLoading(true);
        let response;
        if (id){
            const obj = generateDataEdit();
            response = await putRefData(obj);
        } else {
            const obj = generateDataAdd();
            response = await postRefData(obj);
        }
        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: 'Data Saved.',
                description: "We've saved this data.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigateBack();
                setIsLoading(false);
            }, 1000)
        } else {
            toast({
                title: 'Data failed to save.',
                description: response?.message || "We are unable to save this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const getDataDetail = async () => {
        const response = await getRefDataById(id);
        if (response?.status === HttpStatusCode.Ok) {
            setDataDetail(response.data);
            setIsLoading(false);
        } else {
            setIsLoading(false);
            toast({
                title: 'Failed to load data.',
                description: response.message,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const getDataListType = async () => {
        const response = await getListOfTypeRefData();
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: string) => ({
                label: item,
                value: item,
            }));

            setListType(formattedData);
        }
    };

    useEffect(() => {
        getDataListType();
    }, []);

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id]);

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} cancelButtonText={cancelButtonText} />
                    <div className="form-container">
                        <Formik
                            initialValues={{
                                type: dataDetail?.type || '',
                                order: dataDetail?.order || '',
                                value: dataDetail?.value || '',
                                name: dataDetail?.name || ''
                            }}
                            validationSchema={addRefDataSchema}
                            onSubmit={(value) => {
                                handleSubmit(value);
                            }}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>
                                    <Field name='type'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.type && form.touched.type)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectTypeRefData name='type' handleChange={({ value }: any) => props.setFieldValue('type', value)} value={props.values.type} defaultValue={props.values.type} hasError={form.errors.type && form.touched.type} options={listType} />
                                                        <FormErrorMessage>{String(form.errors.type)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='order'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.order && form.touched.order)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Order<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Order' className="form-input" />
                                                        <FormErrorMessage>{String(form.errors.order)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='value'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.value && form.touched.value)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Value<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Value' className="form-input" />
                                                        <FormErrorMessage>{String(form.errors.value)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='name'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.name && form.touched.name)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Name' className="form-input" />
                                                        <FormErrorMessage>{String(form.errors.name)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor='white'
                                            borderColor='#D0D5DD'
                                            isLoading={isSubmitting}
                                            marginRight={5}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                        >
                                            Save Data
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default EditReferenceData;
